import * as React from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { Button } from './button';
import { Input } from './input';
import { Textarea } from './textarea';

function InputGroup({ className, ...props }) {
    return (
        <div
            data-slot="input-group"
            role="group"
            className={cn(
                'group/input-group border-input dark:bg-input/30 shadow-2xs relative flex w-full items-center rounded-md border-2 outline-hidden transition-[color,box-shadow]',
                'h-9 has-[>textarea]:h-auto',
                // Variants based on alignment.
                '[&>input]:has-[>[data-align=inline-start]]:pl-2',
                '[&>input]:has-[>[data-align=inline-end]]:pr-2',
                'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col [&>input]:has-[>[data-align=block-start]]:pb-3',
                'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col [&>input]:has-[>[data-align=block-end]]:pt-3',
                // Focus state.
                'has-[[data-slot=input-group-control]:focus-visible]:ring-ring has-[[data-slot=input-group-control]:focus-visible]:ring-1',
                // Error state.
                'has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive-2 dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40',
                className
            )}
            {...props}
        />
    );
}

const inputGroupAddonVariants = cva(
    "text-muted-foreground flex h-auto cursor-text select-none items-center justify-center gap-2 py-1.5 text-sm font-medium group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
    {
        variants: {
            align: {
                'inline-start':
                    'order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]',
                'inline-end': 'order-last pr-3 has-[>button]:mr-[-0.4rem] has-[>kbd]:mr-[-0.35rem]',
                'block-start':
                    '[.border-b]:pb-3 order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5',
                'block-end':
                    '[.border-t]:pt-3 order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5',
            },
        },
        defaultVariants: {
            align: 'inline-start',
        },
    }
);

function InputGroupAddon({ className, align = 'inline-start', ...props }) {
    return (
        <div
            role="group"
            data-slot="input-group-addon"
            data-align={align}
            className={cn(inputGroupAddonVariants({ align }), className)}
            onClick={e => {
                if (e.target.closest('button')) {
                    return;
                }
                e.currentTarget.parentElement?.querySelector('input')?.focus();
            }}
            {...props}
        />
    );
}

const inputGroupButtonVariants = cva('flex items-center gap-2 text-sm shadow-none', {
    variants: {
        size: {
            xs: "h-6 gap-1 rounded-[calc(var(--radius)-5px)] px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
            sm: 'h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5',
            'icon-xs': 'size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0',
            'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
        },
    },
    defaultVariants: {
        size: 'xs',
    },
});

const InputGroupButton = React.forwardRef(
    ({ className, type = 'button', variant = 'ghost', size = 'xs', ...props }, ref) => {
        return (
            <Button
                ref={ref}
                type={type}
                data-size={size}
                variant={variant}
                className={cn(inputGroupButtonVariants({ size }), className)}
                {...props}
            />
        );
    }
);
InputGroupButton.displayName = 'InputGroupButton';

function InputGroupText({ className, ...props }) {
    return (
        <span
            className={cn(
                "text-muted-foreground flex items-center gap-2 text-sm [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
                className
            )}
            {...props}
        />
    );
}

function InputGroupInput({ className, ...props }) {
    return (
        <Input
            data-slot="input-group-control"
            className={cn(
                'flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent',
                className
            )}
            {...props}
        />
    );
}

function InputGroupTextarea({ className, ...props }) {
    return (
        <Textarea
            data-slot="input-group-control"
            className={cn(
                'flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none focus-visible:ring-0 dark:bg-transparent',
                className
            )}
            {...props}
        />
    );
}

export {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupText,
    InputGroupInput,
    InputGroupTextarea,
};
